import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, LogIn } from 'lucide-react';

const LOGO_URL = '/advecti-car-wordmark.webp';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const location = useLocation();

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('advecti_token');
    setIsLoggedIn(!!token);
  }, [location]);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/how-it-works', label: 'How It Works' },
    { path: '/for-drivers', label: 'For Drivers' },
    { path: '/for-advertisers', label: 'For Advertisers' },
    { path: '/get-started', label: 'Get Started' },
    { path: '/faq', label: 'FAQ' },
    { path: '/contact', label: 'Contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="bg-cream sticky top-0 z-50 shadow-md" data-testid="navbar">
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-12">
        <div className="flex items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center flex-shrink-0" aria-label="Advecti home" data-testid="navbar-logo">
            <img 
              src={LOGO_URL} 
              alt="Advecti - Advertising on Cars" 
              width={2000}
              height={711}
              className="w-40 md:w-32 lg:w-44 xl:w-48 h-auto object-contain mix-blend-multiply"
              data-testid="navbar-logo-image"
            />
          </Link>

          {/* Desktop Navigation - Evenly distributed */}
          <div className="hidden md:flex items-center justify-around flex-1 min-w-0 ml-4 lg:ml-8" data-testid="navbar-desktop-links">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`font-medium transition-colors ${
                  isActive(link.path)
                    ? 'text-navy border-b-2 border-navy'
                    : 'text-navy/70 hover:text-navy'
                }`}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to={isLoggedIn ? '/dashboard' : '/advertiser-login'}
              className={`flex items-center gap-1.5 font-medium transition-colors ${
                isActive('/advertiser-login') || isActive('/dashboard')
                  ? 'text-navy border-b-2 border-navy'
                  : 'text-navy/70 hover:text-navy'
              }`}
              data-testid="nav-advertiser-login"
            >
              <LogIn size={16} />
              {isLoggedIn ? 'Dashboard' : 'Advertiser Login'}
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden text-navy p-2 ml-auto"
            aria-label={isOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={isOpen}
            aria-controls="navbar-mobile-menu"
            data-testid="mobile-menu-button"
          >
            {isOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <div id="navbar-mobile-menu" className="md:hidden bg-cream border-t border-navy/10" data-testid="mobile-menu">
          <div className="px-4 py-4 space-y-3">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsOpen(false)}
                data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`block py-2 px-4 rounded-lg font-medium ${
                  isActive(link.path)
                    ? 'bg-navy text-cream'
                    : 'text-navy hover:bg-navy/10'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to={isLoggedIn ? '/dashboard' : '/advertiser-login'}
              onClick={() => setIsOpen(false)}
              data-testid="mobile-nav-advertiser-login"
              className={`flex items-center gap-2 py-2 px-4 rounded-lg font-medium ${
                isActive('/advertiser-login') || isActive('/dashboard')
                  ? 'bg-navy text-cream'
                  : 'text-navy hover:bg-navy/10'
              }`}
            >
              <LogIn size={16} />
              {isLoggedIn ? 'Dashboard' : 'Advertiser Login'}
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
